// src/viewmodels/useCustomer360ViewModel.ts
import { useEffect, useRef, useState } from "react";

import { models, factories, service } from "powerbi-client";
import { getCookie } from "../../utils/decrypt";
import { userService } from "../../services/userService";
import logger from "../../utils/logger";
import { SCHEMA, VISUAL_KEY } from "../../constant/appConsatnt";
import { POWER_BI_ACTIVE_TAB_OPTIONS } from "../../constant/enum";

export default function Customer360ViewModel() {
  const reportRef = useRef<any>(null);
  const powerbiService = useRef(
    new service.Service(
      factories.hpmFactory,
      factories.wpmpFactory,
      factories.routerFactory
    )
  );

  const [embedData, setEmbedData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [reportInstance, setReportInstance] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<POWER_BI_ACTIVE_TAB_OPTIONS>(POWER_BI_ACTIVE_TAB_OPTIONS.PATTERNS);

  // Get customer ID from cookie
  useEffect(() => {
    const idFromCookie = getCookie("customerId");
    if (idFromCookie) setCustomerId(idFromCookie);
  }, []);

  // Set default tab for mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setActiveTab(POWER_BI_ACTIVE_TAB_OPTIONS.PATTERNS);
    }
  }, []);

  // Fetch Power BI embed info
  useEffect(() => {
    const fetchEmbedData = async () => {
      try {
        const response:any = await userService.getembedinfo();
        
        const data =  response;
        setEmbedData(data);
        if (!customerId && data.customerId) setCustomerId(data.customerId);
      } catch (err) {
        setError("Failed to fetch Power BI data.");
        logger.error(`${Customer360ViewModel.name} - useEffect (fetchEmbedData)`, error);
      }
    };
    fetchEmbedData();
  }, []);

  // Embed Power BI report
  useEffect(() => {
    if (!embedData || !reportRef.current || !customerId) return;

    powerbiService.current.reset(reportRef.current);

    const reportConfig = {
      type: "report",
      id: embedData.reportId,
      embedUrl: embedData.embedUrl,
      accessToken: embedData.accessToken,
      tokenType: models.TokenType.Embed,
      settings: {
        background: models.BackgroundType.Transparent,
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: models.DisplayOption.FitToPage,
        },
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: true },
        },
      },
    };

    const report = powerbiService.current.embed(reportRef.current, reportConfig) as any;
    setReportInstance(report);

   
    report.on("loaded", async () => {
      try {
        const pages = await report.getPages();
        const activePage = pages[0];
        const visuals = await activePage.getVisuals();

        const slicerVisualName = "e88a5406330704a4134e";
        const slicer = visuals.find((v :any) => v.name === slicerVisualName);

        if (!slicer) {
          setError("Customer ID slicer not found in the report.");
          return;
        }

        await slicer.setSlicerState({
          filters: [
            {
              $schema: "http://powerbi.com/product/schema#basic",
              target: {
                table: "Customer_Dim",
                column: "Id",
              },
              operator: "In",
              values: [customerId],
            },
          ],
        });

        await slicer.setFilters([
          {
            $schema: "http://powerbi.com/product/schema#basic",
            target: {
              table: "Customer_Dim",
              column: "Id",
            },
            operator: "In",
            values: [customerId],
          },
        ]);
      } catch (err) {
        setError("Failed to apply slicer filter.");
      }
    });

    report.on("error", () => {
      setError("Error loading Power BI report.");
    });
  }, [embedData, customerId]);

  // Page switch logic
  useEffect(() => {
    const switchPage = async () => {
      if (!reportInstance) return;
      try {
        const pages = await reportInstance.getPages();
        const targetPage = pages.find((page: any) =>
          activeTab === POWER_BI_ACTIVE_TAB_OPTIONS.PATTERNS
            ? page.displayName.toLowerCase().includes("pattern")
            : page.displayName.toLowerCase().includes("health")
        );
        if (targetPage) {
          await targetPage.setActive();
        } else {
          logger.warn("Page not found:", activeTab);
        }
      } catch (err) {
        logger.error(`${Customer360ViewModel.name} - useEffect (switchPage)`, error);
      }
    };
    switchPage();
  }, [activeTab, reportInstance]);

  return {
    reportRef,
    embedData,
    customerId,
    error,
    activeTab,
    setActiveTab,
  };
}
